import type {
  ApiError,
  ConflictApiError,
  ConflictingElementInfo,
  Filterable,
  Pageable,
  Searchable,
  Sortable,
  SortDirection,
} from '@empathyco/playboard-core-domain'
import type { SetupContext } from 'vue'
import type {
  DataTableOption,
  DateRangeFilterConfig,
  DateRangeFilterSelectedValues,
  Filter,
  Pagination,
  RegularFilterConfig,
  RegularFilterSelectedValues,
  Sort,
} from '../../models'
import type {
  IToolingBaseComposableDataItem,
  IToolingBaseComposableDataItemCriteria,
  IToolingBaseComposableProps,
} from '../use-tooling-base/types'
import type {
  IToolingListComposableMethods,
  SetEnablePayload,
  ToolingListComposableMethodsParams,
} from './types'
// @ts-strict-ignore
import { DomainObjectUtils } from '@empathyco/playboard-core-domain'
import { getCurrentInstance, ref, watch } from 'vue'
import { EnabledFilterEnum, FilterTypeEnum, SortDirectionEnum } from '../../enums'
import { ToolItemAction } from '../../models'
import { ROUTER_CONSTS } from '../../router/router.consts'
import { ENABLED_FILTER_CONFIG } from '../../router/routes.filters'
import { CONTEXT_CONSTS } from '../../store/context/context.consts'
import { TOOLING_CONSTS } from '../../store/tooling/tooling.consts'
import { ConflictErrorsUtils } from '../../utils/conflict-errors.utils'
import { FiltersUtils } from '../../utils/filters.utils'
import { useApiError } from '../use-api-error'
import { mapActions, mapGetters } from '../utils'

export const methodsFn = <DataItemType extends IToolingBaseComposableDataItem>(
  props: IToolingBaseComposableProps,
  { emit }: SetupContext,
  params: ToolingListComposableMethodsParams<DataItemType>,
): IToolingListComposableMethods<DataItemType> => {
  const ctx = getCurrentInstance()

  const { getErrorMessage, isConflictError } = useApiError()

  const contextWatchDisabled = ref(false)

  const selectedItemsCache = ref<Array<any>>([])

  const { context, enabledFilters } = mapGetters(ctx.proxy.$store, CONTEXT_CONSTS.moduleName, {
    context: CONTEXT_CONSTS.getters.context,
    enabledFilters: CONTEXT_CONSTS.getters.enabledFilters,
  })

  const { setQuery, setContextStrictMatching } = mapActions(
    ctx.proxy.$store,
    CONTEXT_CONSTS.moduleName,
    {
      setQuery: CONTEXT_CONSTS.actions.setQuery,
      setContextStrictMatching: CONTEXT_CONSTS.actions.setStrictMatching,
    },
  )

  const { findConflictingItemsAction, listAction, deleteAction, updateAction } = mapActions(
    ctx.proxy.$store,
    TOOLING_CONSTS.moduleName,
    {
      findConflictingItemsAction: TOOLING_CONSTS.actions.findConflictingItems,
      listAction: TOOLING_CONSTS.actions.list,
      deleteAction: TOOLING_CONSTS.actions.delete,
      updateAction: TOOLING_CONSTS.actions.update,
    },
  )

  const getServicePage = (pagination: Pagination): Pageable => {
    return {
      page: {
        start: (pagination.page - 1) * pagination.itemsPerPage,
        rows: pagination.itemsPerPage,
      },
    }
  }

  const getServiceSort = (sort: Sort | undefined): Sortable | undefined => {
    if (!sort) {
      return undefined
    }
    const direction: SortDirection = {
      [SortDirectionEnum.ASCENDING]: 'ASC',
      [SortDirectionEnum.DESCENDING]: 'DESC',
    }[sort.direction] as SortDirection
    return {
      sort: {
        field: sort.field,
        direction,
      },
    }
  }

  const getServiceSearch = (query: string): Searchable => {
    if (!query) {
      return undefined
    }
    return {
      search: {
        query,
      },
    }
  }

  const getStrictMatching = (): Record<string, boolean> => {
    return { strictMatching: params.strictMatching.value }
  }

  function getServiceFilters(filters: Record<string, Filter>): Filterable {
    return Object.values(filters).reduce<Filterable>(
      (result, filter) => {
        if (filter.config.type === FilterTypeEnum.REGULAR) {
          const filterConfig = filter.config as RegularFilterConfig
          const filterSelectedValues = filter.selectedValues as RegularFilterSelectedValues
          if (filterConfig.id === ENABLED_FILTER_CONFIG.id && filterSelectedValues.length > 0) {
            result.filters[filterConfig.id] = {
              [EnabledFilterEnum.ENABLED]: true,
              [EnabledFilterEnum.DISABLED]: false,
            }[filterSelectedValues[0]]
          } else {
            const filterId = FiltersUtils.getEncodedId(filterConfig)
            result.filters[filterId] = filterSelectedValues.map(filterSelectedValue =>
              FiltersUtils.encodeValue(filterConfig, filterSelectedValue),
            )
          }
        } else if (filter.config.type === FilterTypeEnum.DATE_RANGE) {
          const filterConfig = filter.config as DateRangeFilterConfig
          const filterSelectedValues = filter.selectedValues as DateRangeFilterSelectedValues

          result.filters[filterConfig.id] = filterSelectedValues
        }
        return result
      },
      { filters: {} },
    )
  }

  const onSelectionChange = (selection: Array<DataItemType>): void => {
    selectedItemsCache.value = selection.map(item => DomainObjectUtils.clone(item))
    params.selection.value = selection
    params.optionSelected.value = undefined
  }

  const loadData = async (): Promise<void> => {
    params.loadingData.value = true
    try {
      const serviceFilters = getServiceFilters(enabledFilters.value)
      const extraParams = params.loadDataExtraParams?.value ?? {}
      const { browseField, browseValue, filters: extraFilters, ...restExtraParams } = extraParams

      const response = await listAction({
        tool: params.featureId.value,
        params: {
          ...getServicePage(params.pagination.value),
          ...getServiceSort(params.sort.value),
          ...getServiceSearch(context.value.query),
          ...getStrictMatching(),
          ...serviceFilters,
          ...restExtraParams,
          filters: {
            ...serviceFilters.filters,
            ...(extraFilters as Record<string, unknown> | undefined),
            ...(browseField && browseValue
              ? {
                  'context.name': 'browseField,browseValue',
                  'context.browseField.value': browseField,
                  'context.browseValue.value': browseValue,
                }
              : {}),
          },
        },
      })
      params.pagination.value.totalItems = response.numFound
    } catch (error) {
      emit('showError', getErrorMessage(error))
    } finally {
      params.loadingData.value = false
    }
  }

  const navigateToRoute = (routeName: string, dataItem?: DataItemType): void => {
    let id
    if (dataItem) {
      id = dataItem.id
    } else {
      id = params.selection.value.map(selectedItem => selectedItem.id).join() || undefined
    }
    ctx.proxy.$router.push({
      ...ctx.proxy.$route,
      name: routeName,
      params: {
        ...ctx.proxy.$route.params,
        [ROUTER_CONSTS.params.id]: id,
      },
    })
  }

  const onPaginationChange = (pagination: Pagination): void => {
    params.setToolingPagination(pagination)
    loadData()
  }

  const onSortChange = (sort: Sort): void => {
    params.setToolingSorting(sort)
    params.pagination.value.page = 1
    loadData()
  }

  const onSearchChange = async (query: string, disableRouteUpdate = false): Promise<void> => {
    await setQuery(query || undefined)
    if (!disableRouteUpdate) {
      emit('contextChanged')
    }
  }

  const onStrictMatchingChange = (): void => {
    params.strictMatching.value = !params.strictMatching.value
    contextWatchDisabled.value = !context?.value.query?.length

    setContextStrictMatching(params.strictMatching.value)
    emit('contextChanged')
  }

  const onCheckboxClick = (dataItem: DataItemType): void => {
    if (params.readonly.value) {
      navigateToRoute(params.routes.update, dataItem)
    }
  }
  const onRowClick = (dataItem: DataItemType): void => {
    navigateToRoute(params.routes.update, dataItem)
  }

  const onDelete = async (): Promise<void> => {
    params.loadingData.value = true
    try {
      const selectedValues = params.optionSelected.value
        ? [params.optionSelected.value]
        : params.selection.value
      await deleteAction({
        tool: params.featureId.value,
        items: selectedValues,
      })
      emit(
        'showSnackbar',
        selectedValues.length > 1
          ? params.messages.deleteMany.success
          : params.messages.deleteOne.success,
      )
      if (!params.data.value.results.length && params.pagination.value.page > 1) {
        params.pagination.value.page -= 1
      }
    } catch (errors) {
      params.loadingData.value = false
      emit('showError', getErrorMessage(errors[0]))
    } finally {
      params.selection.value = []
      loadData()
    }
  }

  const addCriteriaToSelectedItems = (
    enabled: boolean,
    customSelection: DataItemType[] = undefined,
    customData: DataItemType[] = undefined,
  ): Array<DataItemType & { criteria: IToolingBaseComposableDataItemCriteria }> => {
    const selection =
      customSelection === undefined
        ? params.optionSelected.value
          ? [params.optionSelected.value]
          : params.selection.value
        : customSelection
    const data = customData || [...params.data.value.results, ...selectedItemsCache.value]

    const items = selection
      .filter(({ criteria }) => {
        return criteria.enabled !== enabled
      })
      .map(({ id: selectedId }) => {
        const selectedItem = data.find(dataItem => dataItem.id === selectedId)
        const result = DomainObjectUtils.clone(selectedItem)
        if (!Array.isArray(result.criteria.pivots)) {
          result.criteria.pivots = []
        }
        result.criteria.enabled = enabled
        return result
      })

    return items
  }

  const setEnabled = async ({
    enabled,
    customSelection = undefined,
    customData = undefined,
    errorHandler,
  }: SetEnablePayload<DataItemType>): Promise<void> => {
    params.loadingData.value = true
    const updateItems = addCriteriaToSelectedItems(enabled, customSelection, customData)

    try {
      await updateAction({
        tool: params.featureId.value,
        items: updateItems,
      })
      if (!customSelection) {
        emit(
          'showSnackbar',
          enabled ? params.messages.enable.success : params.messages.disable.success,
        )
        if (params.sort.value) params.setToolingPaginationBasedOnSort(params.sort.value)
      }
    } catch (errors) {
      if (typeof errorHandler === 'function') {
        errorHandler(errors)
      } else {
        emit('showError', getErrorMessage(errors[0]))
      }
    } finally {
      params.loadingData.value = false
      if (!customSelection) {
        params.selection.value = []
        loadData()
      }
    }
  }

  const getPreviousEnabledItems = async (
    selectedItems: Array<
      DataItemType & {
        criteria: IToolingBaseComposableDataItemCriteria
      }
    >,
  ): Promise<Array<DataItemType>> => {
    const responses = await Promise.all(
      selectedItems.map(async item => {
        const pivots = item.criteria.pivots.reduce((_pivots: Record<string, string>, pivot) => {
          return {
            ..._pivots,
            [pivot.name]: pivot.values[0],
          }
        }, {})

        try {
          const responses = await findConflictingItemsAction({
            tool: params.featureId.value,
            params: {
              queries: item.criteria.queries,
              strictMatching: true,
              filters: {
                ...pivots,
                enabled: true,
              },
            },
          })

          return responses.numFound ? responses.results : null
        } catch (error) {
          emit('showError', getErrorMessage(error))
        }
      }),
    )

    return responses.reduce((responses: Array<DataItemType>, response: Array<DataItemType>) => {
      if (response) {
        const filteredResponses = response.filter(
          result => !responses.some(({ id }) => id === result.id),
        )

        responses.push(...filteredResponses)
      }

      return responses
    }, [])
  }

  const setEnabledAndDisabledPrevious = async ({
    enabled,
    callFromModal,
    enableDialog,
  }: SetEnablePayload<DataItemType>): Promise<void> => {
    params.loadingData.value = true

    if (enabled) {
      const selectedItems = addCriteriaToSelectedItems(true)

      const itemsToDisable = await getPreviousEnabledItems(selectedItems)
      if (itemsToDisable.length) {
        if (callFromModal) {
          const selection = params.selection.value
          const data = params.data.value.results

          await setEnabled({
            enabled: false,
            customSelection: itemsToDisable,
            customData: itemsToDisable,
          })

          params.selection.value = selection
          params.data.value.results = data
        } else {
          if (enableDialog) {
            params.loadingData.value = false
            enableDialog.open()
          }

          return
        }
      }
    }

    await setEnabled({ enabled })
  }

  async function setEnabledAndShowConflictErrors(
    payload: SetEnablePayload<DataItemType>,
  ): Promise<void> {
    await setEnabled({
      ...payload,
      errorHandler,
    })
  }

  function errorHandler(errors: ApiError[]): void {
    const conflictingErrors = errors.filter(error => isConflictError(error))
    if (conflictingErrors.length) {
      handleConflictErrors(errors as ConflictApiError[])
    } else {
      handleError(errors)
    }
  }

  function handleConflictErrors(errors: ConflictApiError[]): void {
    const conflictingElements = filterDuplicateConflictingElements(
      errors.flatMap((error: ConflictApiError) => error.conflictingElements),
    )
    const linksToUpdateConflictingElements =
      ConflictErrorsUtils.buildLinksToUpdateConflictingElements(
        ctx.proxy.$router,
        params.routes.update,
        conflictingElements,
      )

    const enableConflictError = params.messages.errors.enableConflictError

    const message = ctx.proxy.$epb.utils.compileMessage(enableConflictError.message, {
      conflictingElements: linksToUpdateConflictingElements
        .map(linkToUpdateConflictingElement => linkToUpdateConflictingElement.outerHTML)
        .join(', '),
    })
    emit('showCustomError', {
      title: enableConflictError.title,
      message,
    })
  }

  function filterDuplicateConflictingElements(
    conflictingElements: ConflictingElementInfo[],
  ): ConflictingElementInfo[] {
    return conflictingElements.reduce((result, conflictingElementInfo) => {
      if (
        !result.find(
          conflictingElementInfo2 => conflictingElementInfo2.id === conflictingElementInfo.id,
        )
      ) {
        result.push(conflictingElementInfo)
      }

      return result
    }, [])
  }

  function handleError(errors: ApiError[]): void {
    emit('showError', getErrorMessage(errors[0]))
  }

  watch(
    context,
    () => {
      if (!contextWatchDisabled.value) {
        params.pagination.value.page = 1
        loadData()
      }

      contextWatchDisabled.value = false
    },
    { deep: true },
  )

  const onOptionClick = async (
    option: DataTableOption,
    dataItem: DataItemType,
    crudListRef: any,
  ): Promise<void> => {
    params.optionSelected.value = dataItem
    if (option.id === ToolItemAction.Edit) {
      navigateToRoute(params.routes.update, dataItem)
    } else if (option.id === ToolItemAction.Copy) {
      navigateToRoute(params.routes.create, dataItem)
    } else if (option.id === ToolItemAction.Delete) {
      crudListRef.value.$refs.deleteDialog.open()
    } else if (option.id === ToolItemAction.Enable) {
      crudListRef.value.onEnable()
    } else if (option.id === ToolItemAction.Disable) {
      await setEnabled({ enabled: false })
    }
  }

  return {
    contextWatchDisabled,
    context,
    getServicePage,
    getServiceSort,
    getServiceSearch,
    getServiceFilters,
    loadData,
    onSelectionChange,
    onPaginationChange,
    onSortChange,
    onSearchChange,
    onStrictMatchingChange,
    onDelete,
    addCriteriaToSelectedItems,
    setEnabled,
    setEnabledAndDisabledPrevious,
    setEnabledAndShowConflictErrors,
    getPreviousEnabledItems,
    onCheckboxClick,
    onRowClick,
    navigateToRoute,
    onOptionClick,
  }
}
